import React, { useState, useEffect } from 'react';
import { StyleSheet, View, FlatList, StatusBar, RefreshControl } from 'react-native';
import { Text } from '@/components/common/text';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useResponsive } from '@/hooks/common/useResponsive';
import { PaymentSummaryProvider } from '@/features/payments/context/PaymentSummaryContext';
import { MobileSegmentedControl } from '@/components/common/residents/mobile/MobileSegmentedControl';
import { MobilePaymentCard } from '@/features/payments/components/mobile/MobilePaymentCard';
import { MobilePaymentCardLightSkeleton } from '@/features/payments/components/mobile/MobilePaymentCardLightSkeleton';
import { MobilePaymentHistoryTab } from '@/features/payments/components/mobile/MobilePaymentHistoryTab';
import { useAppSelector, useAppDispatch } from '@/store';
import { selectLeaseIds, selectConnectionLeaseIds, linkedAcctsConnections, linkedAcctsConnectionsLoading } from '@/store/selectors';
import { useGetLeaseQuery } from '@/store/api';
import { selectLease, hasAutoPay } from '@/features/payments/utils/leaseAutoPay';
import { usePaymentBalance } from '@/features/payments/api/paymentHooks';
import { fetchLinkedAccounts } from '@/store/slices/residents/homeSlice';
import { strings } from '@/constants/residents/strings';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { DesktopPaymentsView } from './desktop';
import {
  useThemeColors,
  useThemedStyles,
  type ThemeContextValue,
} from '@/hooks/residents/useThemeColors';


const MobilePaymentsContent: React.FC = () => {
  const { mode, semantic } = useThemeColors();
  const styles = useThemedStyles(makeStyles);
  const [activeTab, setActiveTab] = useState(0);
  const dispatch = useAppDispatch();
  
  const ownLeaseIds = useAppSelector(selectLeaseIds);
  const connectionLeaseIds = useAppSelector(selectConnectionLeaseIds);
  const linkedAccounts = useAppSelector(linkedAcctsConnections);
  const isLoading = useAppSelector(linkedAcctsConnectionsLoading);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!linkedAccounts || linkedAccounts.length === 0) {
      dispatch(fetchLinkedAccounts());
    }
  }, [dispatch]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await dispatch(fetchLinkedAccounts()).unwrap();
    } catch (error) {
      console.error('Failed to refresh:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const allLeaseIds = [...ownLeaseIds, ...connectionLeaseIds];
  const hasLeases = allLeaseIds.length > 0;

  const renderEmptyState = () => (
    <View style={styles.mobileEmptyState}>
      <Text style={styles.mobileEmptyStateTitle}>
        {strings.mobilePayments.emptyCurrentTitle}
      </Text>
      <Text style={styles.mobileEmptyStateMessage}>
        {strings.mobilePayments.emptyCurrentMessage}
      </Text>
    </View>
  );

  return (
    <View style={styles.mobileContainer}>
      <StatusBar
        barStyle={mode === 'dark' ? 'light-content' : 'dark-content'}
        backgroundColor={semantic.background.primary}
      />
      
      <SafeAreaView edges={['top']} style={styles.mobileHeaderSafeArea}>
        <View style={styles.mobileHeaderTitleRow}>
          <Text style={[styles.mobileHeaderTitle, { color: semantic.text.primary }]}>{strings.mobilePayments.title}</Text>
        </View>
      </SafeAreaView>

      <MobileSegmentedControl
        tabs={[strings.mobilePayments.tabCurrent, strings.mobilePayments.tabHistory]}
        selectedIndex={activeTab}
        onTabChange={setActiveTab}
      />

      {isLoading && !hasLeases ? (
        <LoadingSpinner message="Loading payments..." variant="admin" />
      ) : activeTab === 0 ? (
        <FlatList
          data={allLeaseIds}
          keyExtractor={(item) => `lease-${item}`}
          renderItem={({ item: leaseId }) => (
            <LeasePaymentCard leaseId={leaseId} isConnection={connectionLeaseIds.includes(leaseId)} />
          )}
          contentContainerStyle={styles.mobileListContent}
          ListEmptyComponent={renderEmptyState}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              tintColor={semantic.interactive.primary}
            />
          }
          showsVerticalScrollIndicator={false}
        />
      ) : (
        <MobilePaymentHistoryTab />
      )}
    </View>
  );
};

const LeasePaymentCard: React.FC<{ leaseId: number; isConnection: boolean }> = ({ leaseId, isConnection }) => {
  const authUser = useAppSelector((s) => (s as any).auth?.user);

  const { balanceDue, balanceItems, accountCredit } = usePaymentBalance(leaseId);

  const leaseQuery = useGetLeaseQuery(
    { leaseId, includeTransactions: false, includePayments: true },
    { skip: !leaseId }
  );

  const { isFetching, isError, isSuccess, data } = leaseQuery;

  if (isFetching) {
    return <MobilePaymentCardLightSkeleton />;
  }

  if (isError || !isSuccess || !data) {
    return null;
  }

  const lease = selectLease(data);
  const property = lease?.property || {};
  const users = lease?.users || [];
  const paymentsBlocked = !!lease?.paymentsBlocked;
  const autopayEnabled = hasAutoPay(lease);
  const unitNumberSpace = lease?.unitNumberSpace || lease?.unitNumber || '';

  const connectedPrimary = users?.find((u: any) => u?.id !== authUser?.id && u?.LeaseCustomer?.customerType === 'primary');
  const displayUser = connectedPrimary || users?.find((u: any) => u?.id !== authUser?.id) || users?.[0] || {};

  const residentName = displayUser?.firstName && displayUser?.lastName
    ? `${displayUser.firstName} ${displayUser.lastName}`
    : 'Resident';

  return (
    <MobilePaymentCard
      leaseId={leaseId}
      residentName={residentName}
      propertyName={property?.marketingName}
      unitNumber={unitNumberSpace}
      balanceDue={balanceDue}
      balanceItems={balanceItems}
      accountCredit={accountCredit}
      autopayEnabled={autopayEnabled}
      isConnection={isConnection}
      paymentsBlocked={paymentsBlocked}
    />
  );
};

export const PaymentsScreen: React.FC = () => {
  const { layoutMode } = useResponsive();

  if (layoutMode === 'desktop') {
    return (
      <PaymentSummaryProvider>
        <DesktopPaymentsView />
      </PaymentSummaryProvider>
    );
  }

  return (
    <PaymentSummaryProvider>
      <MobilePaymentsContent />
    </PaymentSummaryProvider>
  );
};

const makeStyles = ({ semantic }: ThemeContextValue) =>
  StyleSheet.create({
    mobileContainer: {
      flex: 1,
      backgroundColor: semantic.background.primary,
    },
    mobileHeaderSafeArea: {
      backgroundColor: semantic.background.primary,
    },
    mobileHeaderTitleRow: {
      paddingHorizontal: 16,
      paddingTop: 8,
      paddingBottom: 4,
      backgroundColor: semantic.background.primary,
    },
    mobileHeaderTitle: {
      fontSize: 18,
      fontWeight: '600',
    },
    mobileListContent: {
      paddingTop: 8,
      paddingBottom: 24,
    },
    mobileEmptyState: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 40,
      paddingTop: 80,
    },
    mobileEmptyStateTitle: {
      fontSize: 18,
      fontWeight: '600',
      color: semantic.text.primary,
      marginBottom: 8,
      textAlign: 'center',
    },
    mobileEmptyStateMessage: {
      fontSize: 14,
      color: semantic.text.tertiary,
      textAlign: 'center',
      lineHeight: 20,
    },
  });
